import { FC, useCallback } from "react";
import { SubmitErrorHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Button } from "~/components/Button";
import { Container } from "~/components/Container";
import { DialogBody } from "~/components/Dialog";
import { Form } from "~/components/Form/Form";
import { FormDialog } from "~/components/FormDialog";
import { FormControl, FieldLabel } from "~/components/FormControl";
import { Col, Row } from "~/components/Grid";
import { TextInput } from "~/components/Input";
import { Text } from "~/components/Text";
import { Alert, AlertIcon } from "~/components/Alert";

export type RequestAgencyPasswordChangeDialogProps = {
  onCloseClick?: () => void;
  onSubmit: (secretCode: string) => void;
};

type FormData = {
  secretKey: string;
};

const schema = yup.object().shape({
  secretKey: yup
    .string()
    .required("Código do Google Authenticator é obrigatório")
    .matches(/^\d{6}$/, "Código deve conter 6 dígitos"),
});

export const RequestAgencyPasswordChangeDialog: FC<RequestAgencyPasswordChangeDialogProps> = ({
  onCloseClick,
  onSubmit,
}) => {
  const { formState, handleSubmit, control } = useForm<FormData>({
    defaultValues: {
      secretKey: "",
    },
    resolver: yupResolver(schema),
    reValidateMode: "onBlur",
    mode: "onSubmit",
  });

  const handleFormSubmit = useCallback(
    (data: FormData) => {
      onSubmit(data.secretKey);
    },
    [onSubmit]
  );

  const onError = useCallback<SubmitErrorHandler<FormData>>(() => {
    // Validação de form - erros já são exibidos nos campos
  }, []);

  return (
    <Container size="8" fixed>
      <Form onSubmit={handleSubmit(handleFormSubmit, onError)}>
        <FormDialog
          title="Solicitar alteração de senha"
          negativeButton={
            <Button variant="tertiary" onClick={onCloseClick}>
              <Text>Cancelar</Text>
            </Button>
          }
          positiveButton={
            <Button disabled={formState.isSubmitting} type="submit">
              <Text>Solicitar</Text>
            </Button>
          }
          onClickDismissButton={onCloseClick}
        >
          <DialogBody css={{ p: "$6", maxHeight: "70vh" }}>
            <Row gap="6">
              <Col sz="12">
                <Alert variant="warning">
                  <AlertIcon />
                  <Text css={{ lineHeight: "1.25" }}>
                    Para solicitar a alteração da sua senha, confirme com o código do Google
                    Authenticator. Você receberá um email com instruções para definir uma nova
                    senha.
                  </Text>
                </Alert>
              </Col>

              <Col sz="12">
                <FormControl name="secretKey" control={control} required>
                  <FieldLabel>Código de segurança</FieldLabel>
                  <TextInput maxLength={6} placeholder="Digite o código de 6 dígitos" autoFocus />
                </FormControl>
                <Text size="2" css={{ mt: "$2", color: "$neutrals-base" }}>
                  Insira o código de 6 dígitos gerado pelo aplicativo Google Authenticator
                </Text>
              </Col>
            </Row>
          </DialogBody>
        </FormDialog>
      </Form>
    </Container>
  );
};

RequestAgencyPasswordChangeDialog.displayName = "RequestAgencyPasswordChangeDialog";
